/**
 * The two LEADER CONTROLS confirmations, as bodies. Desktop wraps them in a
 * `Modal`, the phone column in a `Sheet` — the chrome differs, the questions do
 * not, so the questions live once.
 *
 * `design/GT Crew.dc.html` (the transfer/delete modals) and
 * `design/GT Mobile Crew.dc.html` (their sheet twins).
 *
 * TWO DELIBERATE DEPARTURES FROM THE MIRRORS, both because the mirrors have a
 * defect here rather than a decision:
 *
 * 1. NO "✓ HANDED OVER → NAME" CONFIRMATION LINE. Both mirrors nest it inside
 *    the leader-only gate, and the hand-over demotes you in the same commit — so the
 *    panel unmounts and the line can never be seen by anybody. A toast says it
 *    instead, which is the only surface still on screen afterwards.
 * 2. NO "✓ … DELETED" END STATE. That panel exists only in the stale
 *    `Crew Onboarding.dc.html`, which still carries CO-ADMIN and the superseded
 *    inline owner block. The live mirrors navigate away and toast. Building the
 *    end state would be porting from a file the changelog itself supersedes.
 */

import type { CSSProperties } from 'react';

import { useQd } from '../store/index.ts';
import { ownerVM } from '../store/selectors.ts';
import { PixelButton } from './PixelButton.tsx';

const MONO = 'var(--gt-font-mono)';

export interface ConfirmMetrics {
  bodyFs: string;
  fieldFs: string;
  pad: string;
  gap: string;
}

const BODY = (m: ConfirmMetrics): CSSProperties => ({
  fontFamily: MONO,
  fontSize: m.bodyFs,
  lineHeight: 1.7,
  color: 'var(--gt-text-5)',
});

export function TransferBody({ m }: { m: ConfirmMetrics }): JSX.Element {
  const s = useQd();
  const v = ownerVM(s);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: m.gap, padding: m.pad }}>
      <div style={BODY(m)}>
        Hand {v.crewNameUp} to another member. You drop to a regular player, and only they can
        hand it back — a party has exactly one leader at all times.
      </div>

      {v.canTransfer ? (
        <>
          <select
            value={v.trPick}
            onChange={(e) => s.setOwnForm({ trPick: e.target.value })}
            style={{
              background: 'var(--gt-field)',
              border: '1px solid var(--gt-border-2)',
              color: 'var(--gt-text)',
              fontFamily: MONO,
              fontSize: m.fieldFs,
              padding: '11px 10px',
              outline: 'none',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <option value="">CHOOSE A MEMBER</option>
            {v.candidates.map((c) => (
              <option key={c.key} value={c.key}>
                {c.nameUp}
              </option>
            ))}
          </select>
          <PixelButton
            label={v.trLbl}
            busy={v.trBusy}
            tone="acc2"
            onClick={() => {
              if (!v.trReady) return;
              // The dialog STAYS while this runs. It used to close first, and
              // that unmount was the only thing preventing a second press on an
              // irreversible control — accidental, and it also meant a refusal
              // landed as a toast over a screen that had moved on. `handOver`
              // closes it on success now.
              void s.handOver(v.trPick);
            }}
            fs={8}
            pad="14px 14px"
            center
          />
        </>
      ) : (
        /* Everybody else is either you or still on a temp password — 3020
           refuses those, so offering an empty picker would only fail later. */
        <div style={{ ...BODY(m), color: 'var(--acc2)' }}>
          There is nobody to hand it to yet. A member has to finish their first sign-in before
          they can lead a party.
        </div>
      )}
    </div>
  );
}

export function DeleteCrewBody({ m }: { m: ConfirmMetrics }): JSX.Element {
  const s = useQd();
  const v = ownerVM(s);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: m.gap, padding: m.pad }}>
      <div style={BODY(m)}>
        This disbands {v.crewNameUp} and everything in it — the board, the calls, the comments,
        the playtests. Accounts survive; the party does not. Old invite links stop working and the
        address is never reused.
      </div>
      <div style={{ fontFamily: MONO, fontSize: m.bodyFs, color: 'var(--gt-muted-2)' }}>
        TYPE {v.crewNameUp} TO CONFIRM
      </div>
      <input
        value={v.delTxt}
        onChange={(e) => s.setOwnForm({ delTxt: e.target.value })}
        style={{
          background: 'var(--gt-field)',
          border: '1px solid var(--gt-border-2)',
          color: 'var(--gt-text)',
          fontFamily: MONO,
          fontSize: m.fieldFs,
          padding: '11px 10px',
          outline: 'none',
          width: '100%',
          boxSizing: 'border-box',
        }}
      />
      <PixelButton
        label={v.delLbl}
        busy={v.delBusy}
        onClick={() => {
          // `removeCrew` checks this again and is the one that decides what
          // travels — the client and server gates are not the same comparison
          // and it is the place they are reconciled.
          if (!v.delReady) return;
          // Stays open until the delete lands, same as the transfer above: the
          // panel saying DELETING… is the only thing on screen that knows.
          void s.removeCrew(v.crewId, v.delTxt.trim());
        }}
        tone="acc2"
        fs={8}
        pad="14px 14px"
        center
      />
    </div>
  );
}
